import partition from 'lodash/partition'
import uniq from 'lodash/uniq'
import {exhaustive} from '@freckle/exhaustive'
import {mapMaybes, maybe} from '@freckle/maybe'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mkNonEmpty, mkNonEmptySingleton, nonEmptyToArray} from '@freckle/non-empty'
import {partitionResults} from '@freckle/educator-entities/ts/common/logic/roster-sync'
import {CsvValidationErrors} from '@freckle/educator-entities/ts/common/models/csv-validation-errors'
import type {
  CsvValidationErrorT,
  UnaffirmedValidationErrorT,
} from '@freckle/educator-entities/ts/common/models/csv-validation-errors'
import type {
  SyncResultT,
  SyncResultSuccessT,
  SyncResultFailureT,
  LogPairT,
} from '@freckle/educator-entities/ts/common/models/roster-sync'
import type {
  SyncCsvIdpDataArtifact,
  ParseGroupedError,
  SisGroupingError,
  NamedCsvException,
  SisCsvValidationError,
} from '@freckle/educator-entities/ts/common/models/roster-sync/artifact'
import type {TranslateT} from '@freckle/educator-materials/ts/helpers/translate'
import type {FailedImportErrorT} from '@freckle/school/ts/rostering/components/import-roster/failed-import'

export const ensureSomeErrorIsShown = (
  errors: FailedImportErrorT[],
  t: TranslateT,
): NonEmptyArray<FailedImportErrorT> => mkNonEmpty(errors) ?? onlyUnexpectedError(t)

export const onlyUnexpectedError = (t: TranslateT): NonEmptyArray<FailedImportErrorT> =>
  mkNonEmptySingleton({message: t('IMPORT_ROSTER_UNEXPECTED_ERROR')})

export const failedImportErrorsUnaffirmed = (
  errors: ReadonlyArray<UnaffirmedValidationErrorT>,
): Array<FailedImportErrorT> => errors.map(error => ({message: error.message}))

export const failedImportErrors = (
  fileErrors: Array<CsvValidationErrorT>,
): Array<FailedImportErrorT> =>
  fileErrors.map(csvError => {
    const {message, details} = csvError
    const {fileName, rowNumber} = details

    const schoolId =
      csvError.name === CsvValidationErrors.schoolNotFound
        ? (csvError.details as {schoolId: string}).schoolId
        : null

    const otherRowNumber =
      csvError.name === CsvValidationErrors.duplicateId ||
      csvError.name === CsvValidationErrors.duplicateEmail
        ? (
            csvError.details as {
              otherRowNumber: number
            }
          ).otherRowNumber
        : null

    //NOTE: studentId and studentSisId are both the studentSisId.  Inconsistency will be fixed in backend soon.
    const studentSisId =
      csvError.name === CsvValidationErrors.studentNotEnrolled
        ? (csvError.details as {studentId: string}).studentId
        : csvError.name === CsvValidationErrors.enrollmentStudentNotFound
          ? (csvError.details as {studentSisId: string}).studentSisId
          : null

    const courseSisId =
      csvError.name === CsvValidationErrors.enrollmentCourseNotFound ||
      csvError.name === CsvValidationErrors.courseTeacherNotFound
        ? (csvError.details as {courseSisId: string}).courseSisId
        : null

    const teacherEmail =
      csvError.name === CsvValidationErrors.courseTeacherNotFound
        ? (csvError.details as {teacherEmail: string}).teacherEmail
        : null

    return {
      message,
      metadata: {
        csvFileName: fileName,
        rowNumber,
        otherRowNumber,
        schoolId,
        studentSisId,
        courseSisId,
        teacherEmail,
      },
    }
  })

export function uniqueSchoolIdsFromErrors(errors: Array<CsvValidationErrorT>): Array<string> {
  const schoolNotFoundErrors = mapMaybes(errors, error =>
    error.name === CsvValidationErrors.schoolNotFound ? error : null,
  )

  return uniq(schoolNotFoundErrors.map(error => (error.details as {schoolId: string}).schoolId))
}

export function partitionAffirmedErrors(
  validationErrors: ReadonlyArray<CsvValidationErrorT>,
): [Array<CsvValidationErrorT>, Array<CsvValidationErrorT>] {
  const [unknownSchoolIdErrors, rest] = partition(
    validationErrors,
    error => error.name === CsvValidationErrors.schoolNotFound,
  )

  return [unknownSchoolIdErrors, rest]
}

export function parseFailedLog(
  schoolIdpId: number,
  schoolName: string,
): (log: LogPairT) => FailedImportErrorT {
  return (log: LogPairT) => {
    const {idpId, logMessage, courseId, studentId} = log
    const {message} = logMessage

    return {
      message,
      metadata: {
        schoolIdpId,
        schoolName,
        courseId: courseId ?? undefined,
        studentId: studentId ?? undefined,
        idpId,
      },
    }
  }
}

/**
 * Errors can be reported in both Sync successes and sync Failures.
 * Here, we extract both types
 */

export function failedItemsFromSync(
  results: Array<SyncResultT>,
): null | NonEmptyArray<FailedImportErrorT> {
  const [failures, successes] = partitionResults(results)

  const syncFailureErrors = failures.map((syncResultFailure: SyncResultFailureT) => {
    const {schoolIdpId, failure: failureLogMessage} = syncResultFailure
    const {message} = failureLogMessage

    return {
      message,
      metadata: {
        schoolIdpId,
      },
    }
  })

  const syncSuccessErrors = successes.flatMap((syncResultSuccess: SyncResultSuccessT) => {
    const {failedItems, schoolIdpId, schoolName} = syncResultSuccess
    const {failedStudents, failedTeachers, failedCourseMemberships, failedCourses} = failedItems

    const studentErrors = failedStudents.map(parseFailedLog(schoolIdpId, schoolName))
    const teacherErrors = failedTeachers.map(parseFailedLog(schoolIdpId, schoolName))
    const courseErrors = failedCourses.map(parseFailedLog(schoolIdpId, schoolName))
    const courseMembershipErrors = failedCourseMemberships.map(
      parseFailedLog(schoolIdpId, schoolName),
    )

    return [...studentErrors, ...teacherErrors, ...courseMembershipErrors, ...courseErrors]
  })

  return mkNonEmpty([...syncFailureErrors, ...syncSuccessErrors])
}

export const errorsFromArtifacts = (
  artifacts: SyncCsvIdpDataArtifact[],
  t: TranslateT,
): null | NonEmptyArray<FailedImportErrorT> => {
  const fromSisGroupingError = (error: SisGroupingError): FailedImportErrorT => {
    switch (error.tag) {
      case 'course-teacher-not-found':
        return {
          message: t('IMPORT_ROSTER_ERROR_COURSE_TEACHER_NOT_FOUND'),
          metadata: {
            courseSisId: error.contents.courseSisId,
            teacherEmail: error.contents.teacherEmail,
          },
        }
      case 'enrollment-course-not-found':
        return {
          message: t('IMPORT_ROSTER_ERROR_ENROLLMENT_COURSE_NOT_FOUND'),
          metadata: {
            courseSisId: error.contents.courseSisId,
          },
        }
      case 'enrollment-student-not-found':
        return {
          message: t('IMPORT_ROSTER_ERROR_ENROLLMENT_STUDENT_NOT_FOUND'),
          metadata: {
            studentSisId: error.contents.studentSisId,
          },
        }
      default:
        return exhaustive(error)
    }
  }

  const fromCsvValidationError = ({
    csvFileName,
    errors,
  }: NamedCsvException<SisCsvValidationError>): FailedImportErrorT[] => {
    const fromSisCsvValidationError = (error: SisCsvValidationError): FailedImportErrorT => {
      switch (error.tag) {
        case 'duplicate-email':
          return {
            message: t('IMPORT_ROSTER_ERROR_DUPLICATE_EMAIL'),
            metadata: {
              csvFileName,
              rowNumber: error.rowNumber,
              otherRowNumber: error.rowNumberFirstSeen,
            },
          }
        case 'duplicate-sis-id':
          return {
            message: t('IMPORT_ROSTER_ERROR_DUPLICATE_SIS_ID'),
            metadata: {
              csvFileName,
              rowNumber: error.rowNumber,
              otherRowNumber: error.rowNumberFirstSeen,
            },
          }
        case 'error-message':
          return {
            message: error.message,
            metadata: {
              csvFileName,
            },
          }
        case 'malformed-language':
          return {
            message: t('IMPORT_ROSTER_ERROR_MALFORMED_LANGUAGE'),
            metadata: {
              csvFileName,
              rowNumber: error.rowNumber,
              studentId: error.studentId,
            },
          }
        case 'school-not-found':
          return {
            message: t('IMPORT_ROSTER_ERROR_SCHOOL_NOT_FOUND'),
            metadata: {
              csvFileName,
              rowNumber: error.rowNumber,
            },
          }
        case 'student-not-enrolled':
          return {
            message: t('IMPORT_ROSTER_ERROR_STUDENT_NOT_ENROLLED'),
            metadata: {
              csvFileName,
              rowNumber: error.rowNumber,
              studentId: error.studentId,
            },
          }
        case 'unsupported-language':
          return {
            message: t('IMPORT_ROSTER_ERROR_UNSUPPORTED_LANGUAGE'),
            metadata: {
              csvFileName,
              rowNumber: error.rowNumber,
              studentId: error.studentId,
            },
          }
        default:
          return exhaustive(error)
      }
    }

    return errors.map(fromSisCsvValidationError)
  }

  const fromParseGroupedError = (error: ParseGroupedError): FailedImportErrorT[] => {
    switch (error.tag) {
      case 'grouping-error':
        return [fromSisGroupingError(error.contents)]
      case 'validation-errors':
        return error.contents.flatMap(fromCsvValidationError)
      default:
        return exhaustive(error)
    }
  }

  const fromArtifact = (artifact: SyncCsvIdpDataArtifact): FailedImportErrorT[] => {
    switch (artifact.tag) {
      case 'failed-parsing':
        return artifact.contents.flatMap(fromParseGroupedError)
      case 'sync-results':
        return maybe(() => [], nonEmptyToArray, failedItemsFromSync(artifact.contents))
      default:
        return exhaustive(artifact)
    }
  }

  return mkNonEmpty(artifacts.flatMap(fromArtifact))
}
